<template>
    <div v-if="loaded">
        <rkm-area
            no-margin-top
            title="Filtros"
            type="fieldset"
        >
            <rkm-form
                ref="Form"
                show-legend
            >
                <rkm-row>
                    <rkm-col cols="2">
                        <rkm-form-select
                            :options="form.tipoData"
                            :validations="validations.data"
                            label="Data"
                            show-default-option
                            text-field="data"
                            tooltip-top="Selecione o tipo de data para o filtro. • ‘Entrada’ mostra a data em que a movimentação foi finalizada e impactou o estoque. • ‘Documento’ mostra a data em que o documento (NF, devolução, DTE) foi emitido."
                            v-model="model.tipoData"
                        />
                    </rkm-col>

                    <rkm-col cols="4">
                        <rkm-form-date-picker
                            :range-inline="false"
                            end-label="Até"
                            range
                            start-label="Período de"
                            tooltip-top="Informe o intervalo de datas conforme o tipo selecionado em ‘Data’. O relatório exibirá apenas as entradas dentro desse período."
                            v-model="model.periodo"
                        />
                    </rkm-col>

                    <rkm-col cols="6">
                        <rkm-form-select
                            :options="form.unidades"
                            default-option-text="Todas"
                            label="Unidade"
                            show-default-option
                            text-field="nomeFantasia"
                            tooltip-top="Filtra as entradas registradas por unidade de saúde ou almoxarifado de destino."
                            v-model="model.unidadeId"
                            value-field="id"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col cols="6">
                        <rkm-form-select
                            :disabled="model.unidadeId === null"
                            :options="infraestruturas"
                            default-option-text="Todos"
                            label="Local de atendimento"
                            show-default-option
                            text-field="nome"
                            v-model="model.infraestruturaId"
                            value-field="id"
                        />
                    </rkm-col>

                    <rkm-col cols="6">
                        <rkm-form-select
                            :disabled="model.infraestruturaId === null"
                            :options="centrosCusto"
                            default-option-text="Todos"
                            label="Setor"
                            show-default-option
                            text-field="setorNome"
                            tooltip-top="Permite refinar a busca por setor interno da unidade selecionada."
                            v-model="model.centroCustoId"
                            value-field="id"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col cols="6">
                        <rkm-form-select
                            :options="form.grupos"
                            default-option-text="Todos"
                            label="Grupo"
                            show-default-option
                            text-field="nome"
                            tooltip-top="Permite restringir os resultados a grupos ou subgrupos específicos de materiais."
                            v-model="model.grupoId"
                            value-field="id"
                        />
                    </rkm-col>

                    <rkm-col cols="6">
                        <rkm-form-select
                            :disabled="model.grupoId === null"
                            :options="subgrupos"
                            default-option-text="Todos"
                            label="Subgrupo"
                            show-default-option
                            text-field="nome"
                            tooltip-top="Permite restringir os resultados a grupos ou subgrupos específicos de materiais."
                            v-model="model.subgrupoId"
                            value-field="id"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col cols="6">
                        <rkm-form-select
                            :options="form.fornecedores"
                            default-option-text="Todos"
                            label="Fornecedor"
                            show-default-option
                            text-field="nomeFantasia"
                            tooltip-top="Exibe apenas entradas vinculadas a um fornecedor específico (usado para auditorias de NF)."
                            v-model="model.fornecedorId"
                            value-field="id"
                        />
                    </rkm-col>

                    <rkm-col cols="3">
                        <rkm-form-select
                            :options="form.tiposDocumento"
                            label="Tipo do documento"
                            show-default-option
                            text-field="nome"
                            tooltip-top="Filtra as entradas por tipo de documento (NF, devolução, DTE etc.). Entradas sem documento são marcadas como ‘SEM DOC’."
                            v-model="model.tipoDocumentoId"
                            value-field="id"
                        />
                    </rkm-col>

                    <rkm-col cols="3">
                        <rkm-form-input
                            label="Número do documento"
                            number
                            tooltip-top="Permite buscar uma nota fiscal ou documento específico."
                            v-model="model.numero_pedido"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col cols="6">
                        <rkm-form-input-autocomplete
                            :repository="repositories.material"
                            :request-query-string-params="autocompleteQueryStringParams.material"
                            :search-fields="autocompleteFields.material"
                            label="Material"
                            text-field="descricaoCompleta"
                            tooltip-text="Permite localizar movimentações de um material específico pelo nome ou código."
                            uppercase
                            v-model="model.material_id"
                        />
                    </rkm-col>

                    <rkm-col cols="3">
                        <rkm-form-select
                            :options="form.tipos"
                            label="Tipo da Entrada"
                            show-default-option
                            text-field="nome"
                            tooltip-top="Filtra o tipo de origem da movimentação (Compra, Transferência, Inventário, Devolução, Estorno). Tipos sem documento aparecem como ‘SEM DOC’."
                            v-model="model.tipoEntradaId"
                            value-field="id"
                        />
                    </rkm-col>

                    <rkm-col cols="auto">
                        <rkm-button
                            :label="false"
                            @click="$refs.Form.validate(search)"
                            color="primary"
                        >
                            Buscar
                        </rkm-button>

                        <rkm-button
                            :label="false"
                            @click="reset"
                        >
                            Limpar
                        </rkm-button>
                    </rkm-col>
                </rkm-row>

            </rkm-form>
        </rkm-area>

        <rkm-area
            no-margin-top
            title="Resultado"
        >
            <template v-slot:trailing>
                <rkm-dropdown
                    class="mr-2"
                    color="primary"
                    cols="1"
                    menu-class="auto-scroll"
                    no-caret
                    small
                    title="Colunas"
                    v-rkm-tooltip.hover
                >
                    <template v-slot:button-content>
                        <rkm-icon icon="columns" />
                    </template>

                    <rkm-form-checkbox
                        :indeterminate="indeterminate"
                        @change="selectAll"
                        class="mx-2"
                        v-model="selectedAll"
                    >
                        Todas
                    </rkm-form-checkbox>

                    <rkm-form-checkbox-group
                        :options="optionalColumns"
                        class="mx-2"
                        stacked
                        text-field="label"
                        v-model="selectedCols"
                        value-field="key"
                    />
                </rkm-dropdown>

                <rkm-button
                    :disabled="itens.length === 0"
                    @click="print"
                    color="info"
                    small
                    title="Imprimir"
                >
                    <rkm-icon icon="print" />
                </rkm-button>

                <rkm-button
                    :disabled="itens.length === 0"
                    @click="exportTo"
                    color="info"
                    small
                    title="Exportar"
                >
                    <rkm-icon icon="file-export" />
                </rkm-button>
            </template>

            <rkm-table
                :fields="fields"
                :foot-clone="itens.length > 0"
                :items="itens"
                no-footer-sorting
                sort-by=""
                @sort-changed="onSortChanged"
            >
                <template #foot(data)>
                    <span></span>
                </template>

                <template #foot(finalizadaEm)>
                    <span></span>
                </template>

                <template #foot(unidade)>
                    <span></span>
                </template>

                <template #foot(infraestrutura)>
                    <span></span>
                </template>

                <template #foot(entrada)>
                    <span></span>
                </template>

                <template #foot(documento)>
                    <span></span>
                </template>

                <template #foot(numeroPedido)>
                    <span></span>
                </template>

                <template #foot(numero)>
                    <span></span>
                </template>

                <template #foot(nomeFantasia)>
                    <span></span>
                </template>

                <template #foot(grupo)>
                    <span></span>
                </template>

                <template #foot(subgrupo)>
                    <span></span>
                </template>

                <template #foot(material)>
                    <span></span>
                </template>

                <template #foot(codigoMaterial)>
                    <span></span>
                </template>

                <template #foot(lote)>
                    <span></span>
                </template>

                <template #foot(validadeLote)>
                    <span></span>
                </template>

                <template #foot(quantidade)>
                    <span></span>
                </template>

                <template #foot(valorUnitario)>
                    <span>{{ getValorFormatted(totalizadores.valorUnitario) }}</span>
                </template>

                <template #foot(valorTotal)>
                    <span>{{ getValorFormatted(totalizadores.total) }}</span>
                </template>

                <template #afterTable>
                    <div class="my-2">
                        <h5>Orientações</h5>
                        <p class="mb-0">
                            <strong>Data do documento</strong> Data de emissão fiscal do documento (quando aplicável). Movimentos sem documento mostram ‘—’.
                        </p>
                        <p class="mb-0">
                            <strong>Finalizada em</strong> Data em que a entrada foi efetivamente confirmada e contabilizada no estoque e no balancete.
                        </p>
                        <p class="mb-0">
                            <strong>Valor total</strong> Soma do valor unitário x quantidade. Exibido em reais (R$). Linhas com valor 0,00 indicam entradas sem valor financeiro.
                        </p>
                        <p class="mb-0">
                            <strong>Total Quantidade / Total Financeiro</strong> Apresenta o total físico (por unidade de medida) e o total financeiro (R$) do período consultado.
                        </p>
                    </div>
                </template>
            </rkm-table>
        </rkm-area>
    </div>
</template>

<script>
import formatsFilter from '@/filters/formats'
import { mapActions } from 'vuex'
import { repository, error, data, object, stringCase, file } from '@/mixins'

export default {
    computed: {
        validations() {

            return {
                data: {
                    required: true,
                },
            }
        },
        autocompleteQueryStringParams() {

            return {
                material: { }
            }
        },
        autocompleteFields() {

            return {
                material: [
                    { key: 'codigo_material', searchable: true },
                    { key: 'descricao', searchable: true },
                ],

            }
        },
        optionalColumns() {

            return this.columns.filter(c => !(c.fixed || false))
        },
        fields() {

            return this.columns.filter(c => (c.fixed || false) || this.selectedCols.includes(c.key))
        },
        infraestruturas() {

            if (this.model.unidadeId !== null) {

                return this.form.unidades.find(u => u.id === this.model.unidadeId).infraestruturas
            }

            return []
        },
        centrosCusto() {

            if (this.model.infraestruturaId !== null && this.infraestruturas.length > 0) {

                const infrasestrutura = this.infraestruturas.find(i => i.id === this.model.infraestruturaId)

                return infrasestrutura ? infrasestrutura.centrosCusto : []
            }

            return []
        },
        subgrupos() {

            if (this.model.grupoId !== null) {

                return this.form.grupos.find(g => g.id === this.model.grupoId).subgrupos
            }

            return []
        },
    },
    created() {

        this.repositories = {
            material: this.getConectaSusRepository('cadastro.material'),
            relatorioEntrada: this.getConectaSusRepository('almoxarifado.relatorioEntrada'),
            impressao: this.getConectaSusRepository('impressao.almoxarifado'),
            exportacao: this.getConectaSusRepository('exportacao.almoxarifado'),
        }

        this.load()
    },
    data() {

        return {
            loaded: false,
            additionals: null,
            form: null,
            defaultModel: null,
            model: null,
            selectedAll: false,
            indeterminate: true,
            sortField: null,
            sortDirection: null,
            selectedCols: [
                'unidade',
                'infraestrutura',
                'lote',
                'quantidade',
                'valorTotal',
            ],
            columns: [
                {
                    key: 'data',
                    label: 'Data do documento',
                    formatter: value => this.isNullOrEmptyString(value) ? '' : this.formatDate(value),
                    filterByFormatted: true,
                    sortable: true,
                },
                {
                    key: 'finalizadaEm',
                    label: 'Finalizada em',
                    sortable: true,
                    formatter: value => this.formatDate(value),
                    filterByFormatted: true,
                },
                {
                    key: 'unidade',
                    label: 'Unidade',
                    sortable: true,
                },
                {
                    key: 'infraestrutura',
                    label: 'Local de atendimento',
                    sortable: true,
                },
                {
                    key: 'entrada',
                    label: 'Tipo da entrada',
                    sortable: true,
                },
                {
                    key: 'documento',
                    label: 'Tipo do documento',
                    sortable: true,
                },
                {
                    key: 'numeroPedido',
                    label: 'Número do Pedido',
                    sortable: true,
                },
                {
                    key: 'numero',
                    label: 'Número do documento',
                    sortable: true,
                },
                {
                    key: 'nomeFantasia',
                    label: 'Fornecedor',
                    sortable: true,
                },
                {
                    key: 'grupo',
                    label: 'Grupo',
                    sortable: true,
                    fixed: true,
                },
                {
                    key: 'subgrupo',
                    label: 'Subgrupo',
                    sortable: true,
                },
                {
                    key: 'codigoMaterial',
                    label: 'Código do material',
                    sortable: true,
                    fixed: true,
                },
                {
                    key: 'material',
                    label: 'Material',
                    sortable: true,
                    fixed: true,
                },
                {
                    key: 'lote',
                    label: 'Lote',
                    sortable: true,
                },
                {
                    key: 'validadeLote',
                    label: 'Validade',
                    sortable: true,
                    formatter: value => this.isNullOrEmptyString(value) ? '' : this.formatDate(value),
                    filterByFormatted: true,
                },
                {
                    key: 'quantidade',
                    label: 'Quantidade',
                    sortable: true,
                },
                {
                    key: 'valorUnitario',
                    label: 'Valor unitário',
                    formatter: value => this.getValorFormatted(value),
                    filterByFormatted: true,
                    sortable: true,
                    fixed: true,
                },
                {
                    key: 'valorTotal',
                    label: 'Valor total',
                    formatter: value => this.getValorFormatted(value),
                    filterByFormatted: true,
                    sortable: true,
                },
            ],
            itens: [],
            totalizadores: null
        }
    },
    methods: {
        ...formatsFilter,
        ...mapActions(['setLoading', 'unsetLoading']),
        load() {

            this.setLoading()

            this.repositories.relatorioEntrada.list()
                .then(({ data }) => {

                    this.additionals = data.additionals
                    this.form = data.form
                    this.model = data.model
                    this.defaultModel = this.clone(data.model)

                    this.addWatchers()

                    this.loaded = true
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        addWatchers() {

            this.$watch('model.unidadeId', function () {

                this.model.infraestruturaId = null
            })

            this.$watch('model.infraestruturaId', function () {

                this.model.centroCustoId = null
            })

            this.$watch('model.grupoId', function () {

                this.model.material = null
                this.model.subgrupoId = null
            })

            this.$watch('model.subgrupoId', function () {

                this.model.material = null
            })
        },
        search() {

            this.setLoading()

            this.repositories.relatorioEntrada.index(this.prepareData())
                .then(({ data }) => {

                    this.itens = data.consulta
                    this.totalizadores = data.totalizadores
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        reset() {

            this.model = this.clone(this.defaultModel)

            this.$nextTick().then(() => this.$refs.Form.resetValidator())
        },
        print() {

            this.setLoading()

            this.repositories.impressao.relatorioEntrada(this.prepareDataWithColumns())
                .then(({ data, headers }) => this.openFile(data, headers['content-type']))
                .finally(() => this.unsetLoading())
        },
        exportTo() {

            this.setLoading()

            this.repositories.exportacao.relatorioEntrada(this.prepareDataWithColumns())
                .then(({ data, headers }) => this.openFile(data, headers['content-type']))
                .finally(() => this.unsetLoading())
        },
        prepareDataWithColumns() {

            const data = this.prepareData()

            data.selectedCols = this.fields.map(f => {

                if (f.key === 'finalizadaEm') {
                    return {
                        key: this.camelToSnake(f.key),
                        label: f.label,
                        class: f.class,
                        dataFormat: 'DD/MM/YYYY'
                    }
                }
                return {
                    key: this.camelToSnake(f.key),
                    label: f.label,
                    class: f.class
                }
            })

            return data
        },
        prepareData() {

            const data = this.clone(this.model)

            this.formateDates(data.periodo, ['start', 'end'], 'DD/MM/YYYY')
            this.createIdKeysToObject(data, ['material'])

            const prepared = this.camelObjectKeysToSnakeRecursive(data)

            if (this.sortField) {
                prepared.sort_field = this.sortField
                prepared.sort_direction = this.sortDirection
            }

            return prepared;
        },
        selectAll(selected) {

            this.selectedCols = selected ? this.columns.map(c => c.key) : []
        },
        getValorFormatted(valor) {
            return this.formatMoney(Number.parseFloat(valor), 6)
        },
        onSortChanged({ sortBy, sortDesc }) {
            this.sortField = sortBy
            this.sortDirection = sortDesc ? 'desc' : 'asc'
        },
    },
    mixins: [repository, error, data, object, stringCase, file],
    watch: {
        model: {
            deep: true,
            handler() {

                this.itens = []
                this.totalizadores = null
            }
        },
        selectedCols(newValue) {

            this.selectedAll = newValue.length > 0 && newValue.length == this.columns.length
            this.indeterminate = newValue.length > 0 && newValue.length != this.columns.length
        },
    }
}
</script>

<style scoped>
.dropdown >>> .auto-scroll {
    max-height: 400px;
    overflow-y: auto;
    width: 400px;
}
</style>
